import { useRef, useState } from 'react'
import { Glyph } from '@/components/r5/glyphs'
import { WeekStrip, weekDays } from '@/components/r5/Meters'
import { Popover } from '@/components/r5/Overlay'
import { RollingNumber } from '@/components/r5/count'
import { dateLocale, t, tCount, useLanguage } from '@/lib/i18n'
import { usePractisedDays, useStudentStats } from './streak'

/**
 * The streak pill in the student top bar (APP-DESIGN-DIRECTION 4.1): the chevron-flame glyph and the current streak from the
 * dashboard stats. Hidden when there is no streak to show. It opens the streak card: the number, "day streak" and this
 * week's strip from completed-attempt dates. Nothing here is invented: no freezes, no weekly goal.
 */
export function StreakPill({ compact = false }: { compact?: boolean }) {
  useLanguage()
  const { stats } = useStudentStats()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const days = usePractisedDays(open)
  const streak = stats?.currentStreak ?? 0
  if (!stats || !Number.isFinite(streak) || streak < 1) return null
  const dayName = (date: Date) => date.toLocaleDateString(dateLocale(), { weekday: 'narrow' })
  return (
    <>
      <button
        ref={ref}
        type="button"
        className="r5-streak r5-tap"
        aria-expanded={open}
        aria-label={tCount(streak, 'Streak: {0} day', 'Streak: {0} days')}
        onClick={() => setOpen((value) => !value)}
      >
        <Glyph name="streak" state="active" size={18} />
        <RollingNumber value={String(streak)} />
        {!compact && <span className="r5-streak__unit" aria-hidden="true">{t('days')}</span>}
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchorRef={ref} label={t('Your streak')} width={340} className="r5-streakcard">
        <div className="r5-streakcard__head">
          <span className="r5-streakcard__flame" aria-hidden="true"><Glyph name="streak" state="active" size={40} /></span>
          <div>
            <p className="r5-streakcard__num"><bdi className="num">{streak}</bdi></p>
            <p className="r5-streakcard__label">{t('day streak')}</p>
          </div>
        </div>
        {days && <WeekStrip days={weekDays(days, new Date(), dayName)} play={open} className="r5-streakcard__week" />}
        <p className="r5-streakcard__foot">{t('Practise once a day to keep it going.')}</p>
      </Popover>
    </>
  )
}
